import { CalendarClock, Check, MapPin, Star, X } from "@tamagui/lucide-icons";
import { XStack, YStack } from "tamagui";

import type {
  ZocdocProviderResult,
  ZocdocProviderReviews,
} from "@/api/generated/models";
import { Button, Heading, LoadingSpinner, Paragraph } from "@/components";
import ErrorLabel from "@/components/forms/ErrorLabel";
import Image from "@/components/Image";
import { formatDateOnly } from "@/utils/datetime";
import { PhaseContainer } from "./PhaseContainer";

type ProvidersPhaseProps = {
  providers: ZocdocProviderResult[];
  carrierName: string | null;
  onSelectProvider: (provider: ZocdocProviderResult) => void;
  onChangeInsurance: () => void;
  onChangeZip: () => void;
  availabilityLoading: boolean;
  isAvailabilityError: boolean;
};

// Zocdoc API returns protocol-relative URLs
const formatPhotoUrl = (url: string) =>
  url.startsWith("//") ? `https:${url}` : url;

const networkLabel = (status: string | null | undefined) => {
  if (status === "accepted") return "In network";
  if (status === "not_accepted") return "Out of network";
  return null;
};

const buildLocationLine = (provider: ZocdocProviderResult) => {
  const distance =
    provider.distance_to_patient_mi != null
      ? `${provider.distance_to_patient_mi.toFixed(1)} mi`
      : null;
  const address =
    [provider.address, provider.city, provider.state]
      .filter(Boolean)
      .join(", ") || null;
  return [distance, address].filter(Boolean).join(" - ");
};

type ProviderCardProps = {
  provider: ZocdocProviderResult;
  carrierName: string | null;
  onSelectProvider?: (provider: ZocdocProviderResult) => void;
  reviews?: ZocdocProviderReviews | null;
};

export const ProviderCard = ({
  provider,
  carrierName,
  onSelectProvider,
  reviews,
}: ProviderCardProps) => {
  const network = networkLabel(provider.accepts_patient_insurance);
  const locationLine = buildLocationLine(provider);
  const showRating =
    reviews != null &&
    reviews.total_reviews > 0 &&
    reviews.average_overall_rating != null;
  const content = (
    <>
      <XStack gap="$200" alignItems="center">
        <YStack
          width="$800"
          height="$800"
          borderRadius="$400"
          backgroundColor="$fillCallout"
          overflow="hidden"
          alignItems="center"
          justifyContent="center"
        >
          {provider.provider_photo_url && (
            <Image
              source={{ uri: formatPhotoUrl(provider.provider_photo_url) }}
              width="$800"
              height="$800"
            />
          )}
        </YStack>
        <YStack flex={1}>
          <Heading size="$sm">
            {provider.full_name ?? "Unknown provider"}
          </Heading>
          {(provider.specialties ?? []).length > 0 && (
            <Paragraph size="$sm" color="$textSecondary">
              {provider.specialties!.join(", ")}
            </Paragraph>
          )}
          {showRating && (
            <XStack gap="$50" alignItems="center">
              <Star size={14} fill="currentColor" />
              <Paragraph size="$sm" color="$textSecondary">
                {reviews!.average_overall_rating!.toFixed(2)} -{" "}
                {reviews!.total_reviews} reviews
              </Paragraph>
            </XStack>
          )}
        </YStack>
      </XStack>
      {locationLine && (
        <XStack gap="$100" alignItems="center">
          <MapPin size={16} color="$textSecondary" />
          <Paragraph size="$sm" color="$textSecondary" flex={1}>
            {locationLine}
          </Paragraph>
        </XStack>
      )}
      {network && carrierName && (
        <XStack gap="$100" alignItems="center">
          {provider.accepts_patient_insurance === "accepted" ? (
            <Check size={16} color="$green" />
          ) : (
            <X size={16} color="$red" />
          )}
          <Paragraph size="$sm" color="$textSecondary" flex={1}>
            {network} - {carrierName}
          </Paragraph>
        </XStack>
      )}
      <XStack gap="$100" alignItems="center">
        <CalendarClock size={16} color="$textSecondary" />
        <Paragraph size="$sm" color="$textSecondary" flex={1}>
          {provider.first_availability_date
            ? `Next available ${formatDateOnly(provider.first_availability_date)}`
            : "No upcoming availability"}
        </Paragraph>
      </XStack>
    </>
  );

  if (!onSelectProvider) {
    return <YStack gap="$50">{content}</YStack>;
  }

  return (
    <Button.Wrapper
      onPress={() => onSelectProvider(provider)}
      dataTrackingId="zocdoc-provider"
      backgroundColor="$background"
      borderRadius="$150"
      borderWidth="$strokeWidth"
      borderColor="$strokeNeutral"
      padding="$200"
      gap="$50"
      flexDirection="column"
      alignItems="stretch"
    >
      {content}
    </Button.Wrapper>
  );
};

export const ProvidersPhase = ({
  providers,
  carrierName,
  onSelectProvider,
  onChangeInsurance,
  onChangeZip,
  availabilityLoading,
  isAvailabilityError,
}: ProvidersPhaseProps) => {
  if (availabilityLoading) {
    return (
      <PhaseContainer
        title="Choose a provider"
        currentStep={3}
        onBack={onChangeZip}
      >
        <YStack alignItems="center" justifyContent="center" padding="$400">
          <LoadingSpinner size={32} />
        </YStack>
      </PhaseContainer>
    );
  }
  return (
    <PhaseContainer
      title="Choose a provider"
      currentStep={3}
      onBack={onChangeZip}
    >
      {providers.length === 0 ? (
        <Paragraph size="$sm" color="$textSecondary">
          No providers found in this area.
        </Paragraph>
      ) : (
        <YStack gap="$150">
          {providers.map((provider) => (
            <ProviderCard
              key={provider.provider_location_id}
              provider={provider}
              carrierName={carrierName}
              onSelectProvider={onSelectProvider}
            />
          ))}
        </YStack>
      )}
      {isAvailabilityError && (
        <ErrorLabel>Failed to load availability. Please try again.</ErrorLabel>
      )}
    </PhaseContainer>
  );
};
